import React from 'react'
import { Box, Button, Field, Flex, Input, Message, Text } from '@workwhile/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Header } from 'components/Header'
import { IndeedFlexAuthStatusBanner } from 'pages/IndeedFlex/shared/components/IndeedFlexAuthStatusBanner'
import { useIndeedFlexOtpContextValue } from '../context/IndeedFlexOtpProvider'
import { indeedFlexOtpSchema, type IndeedFlexOtpFormValues } from '../schema'

export const IndeedFlexOtpMain = () => {
  const {
    state: { requestState, submitState },
    actions: { requestChallenge, submitCode },
  } = useIndeedFlexOtpContextValue()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IndeedFlexOtpFormValues>({
    resolver: zodResolver(indeedFlexOtpSchema),
    defaultValues: { code: '' },
  })

  const challenge = requestState.data

  const onSubmit = handleSubmit((values) => {
    if (!challenge) {
      return
    }
    submitCode({
      challengeToken: challenge.challengeToken,
      code: values.code,
    })
  })

  return (
    <>
      <Header title="Indeed Flex email code" />
      <IndeedFlexAuthStatusBanner showWhenLive />
      <Box margin={3} maxWidth={560}>
        <Text fontSize={2}>
          Request a code from Indeed Flex, then enter the code from the email to
          restore the integration session.
        </Text>
        <Flex mt={3}>
          <Button
            type="button"
            variant="primary"
            loading={requestState.isPending}
            disabled={requestState.isPending}
            onClick={() => requestChallenge()}
          >
            Send email code
          </Button>
        </Flex>
        {requestState.isError && requestState.errorMessage ? (
          <Box mt={3}>
            <Message variant="error">{requestState.errorMessage}</Message>
          </Box>
        ) : null}
        {challenge ? (
          <Box as="form" mt={4} onSubmit={onSubmit}>
            <Text fontSize={2}>
              Code expires {challenge.challengeTokenExpiresAt}.
            </Text>
            <Box mt={3}>
              <Field label="Email code" error={errors.code?.message}>
                <Input
                  {...register('code')}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />
              </Field>
            </Box>
            <Flex mt={3}>
              <Button
                type="submit"
                variant="primary"
                loading={submitState.isPending}
                disabled={submitState.isPending}
              >
                Submit code
              </Button>
            </Flex>
          </Box>
        ) : null}
        {submitState.isError && submitState.errorMessage ? (
          <Box mt={3}>
            <Message variant="error">{submitState.errorMessage}</Message>
          </Box>
        ) : null}
        {submitState.isSuccess ? (
          <Box mt={3}>
            <Message variant="success">
              Indeed Flex session saved. Sync can continue.
            </Message>
          </Box>
        ) : null}
      </Box>
    </>
  )
}
